import type { SidebarSectionRowComponent } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"

import { BaseComponent } from "./base-component"

interface Props {
	component: SidebarSectionRowComponent
}

const BUTTON_TYPES = new Set<
	SidebarSectionRowComponent["right"][number]["type"]
>(["actionButton", "linkButton", "copyButton"])

export function RowComponent({ component }: Props) {
	// Buttons sit side by side and wrap; values (text, ids) stack.
	const buttonsOnly =
		component.right.length > 0 &&
		component.right.every((c) => BUTTON_TYPES.has(c.type))

	if (buttonsOnly && component.left.length === 0) {
		return (
			<div className="flex w-full min-w-0 flex-wrap gap-1">
				{component.right.map((c, i) => (
					<BaseComponent key={i} component={c} inRow />
				))}
			</div>
		)
	}

	// A value hugs its content but never past 55%, so a long id truncates
	// instead of wrapping the label onto three lines. Buttons get the room the
	// short label leaves.
	return (
		<div
			className={cn(
				"grid min-h-6 w-full items-center gap-3",
				buttonsOnly
					? "grid-cols-[fit-content(45%)_minmax(0,1fr)]"
					: "grid-cols-[minmax(0,1fr)_minmax(0,55%)]",
			)}
		>
			{/* The left cell is the row's label, so it stays out of a text selection. */}
			<div className="flex min-w-0 select-none flex-col gap-0.5">
				{component.left.map((c, i) => (
					<BaseComponent key={i} component={c} inRow />
				))}
			</div>
			<div
				className={cn(
					"flex min-w-0",
					buttonsOnly
						? "flex-row flex-wrap justify-end gap-1"
						: "flex-col items-end gap-0.5",
				)}
			>
				{component.right.map((c, i) => (
					<BaseComponent key={i} component={c} inRow />
				))}
			</div>
		</div>
	)
}
